/**
 * Geometry of the highlight painted while a toolbar control holds the text
 * session open. The browser only draws the native selection in the focused
 * element, and neither an inline decoration nor a CSS custom highlight paints
 * mixed-size lines the way the native selection does (Chromium covers each
 * run's glyph box, not the line box), so the overlay measures the selection
 * out of the live DOM and paints its own rectangles: one per selected line,
 * stretched to the full line-box height every run on the line shares.
 */

import type { EditorView } from "@tiptap/pm/view";

/** Viewport-space sides of one measured fragment, line, or line box. */
export interface RectSides {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

/** Vertical extent of a block's content box, which its line boxes tile. */
export interface ContentBounds {
  top: number;
  bottom: number;
}

/** One held-selection rectangle, positioned relative to the edit surface. */
export interface HeldSelectionRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

/** The empty measurement, shared so an unchanged result keeps its identity. */
export const NO_HELD_SELECTION_RECTS: HeldSelectionRect[] = [];

/**
 * Measures the rectangles the held selection paints with: the selection's
 * per-line fragments widened to nothing, and raised to the full line-box
 * height derived from the surrounding block.
 * @param view - The session's editor view
 * @param surface - The edit surface the rectangles are positioned relative to
 * @returns The rectangles, empty when there is no measurable range selection
 */
export function measureHeldSelectionRects(
  view: EditorView,
  surface: HTMLElement,
): HeldSelectionRect[] {
  const { from, to } = view.state.selection;
  if (from === to || view.isDestroyed || !view.dom.isConnected) {
    return NO_HELD_SELECTION_RECTS;
  }
  const selection = rangeBetween(view, from, to);
  const selectionLines = groupRectsIntoLines(fragmentSides(selection));
  if (selectionLines.length === 0) {
    return NO_HELD_SELECTION_RECTS;
  }
  const lineBoxes = blocksIntersecting(view.dom, selection).flatMap(
    blockLineBoxes,
  );
  const surfaceRect = surface.getBoundingClientRect();
  return selectionLines.map((line) => {
    const box = bestVerticalOverlap(line, lineBoxes) ?? line;
    return {
      top: box.top - surfaceRect.top,
      left: line.left - surfaceRect.left,
      width: line.right - line.left,
      height: box.bottom - box.top,
    };
  });
}

/**
 * Whether two measurements paint identically, so an unchanged frame can keep
 * the previous state and skip a re-render.
 * @param a - One measurement
 * @param b - The other measurement
 * @returns True when every rectangle matches
 */
export function sameHeldSelectionRects(
  a: HeldSelectionRect[],
  b: HeldSelectionRect[],
): boolean {
  return (
    a.length === b.length &&
    a.every(
      (rect, index) =>
        rect.top === b[index].top &&
        rect.left === b[index].left &&
        rect.width === b[index].width &&
        rect.height === b[index].height,
    )
  );
}

/**
 * Groups fragment rectangles into one union per visual line. Fragments on the
 * same line overlap vertically however their sizes differ (their glyph boxes
 * share a baseline), while fragments of distinct lines do not.
 * @param rects - The measured fragment rectangles, in any order
 * @returns One union per line, ordered top to bottom
 */
export function groupRectsIntoLines(rects: RectSides[]): RectSides[] {
  const sorted = [...rects].sort((a, b) => a.top - b.top);
  return sorted.reduce<RectSides[]>((lines, rect) => {
    const line = lines[lines.length - 1];
    if (line === undefined || rect.top >= line.bottom) {
      return [...lines, rect];
    }
    return [...lines.slice(0, -1), unionOf(line, rect)];
  }, []);
}

/**
 * Stretches a block's glyph-box line unions out to its line-box bounds: the
 * first line up to the content-box top, the last down to the content-box
 * bottom, and each interior boundary to the midpoint of the gap between the
 * neighboring unions. Line boxes tile the content box, so the edges are exact
 * and an interior boundary is off by at most half the difference of the two
 * lines' leadings - invisible whenever both sides are selected, because the
 * painted rectangles still tile.
 * @param lines - The block's per-line unions, ordered top to bottom
 * @param content - The block's content-box vertical bounds
 * @returns The lines with their vertical sides at line-box bounds
 */
export function expandLinesToContent(
  lines: RectSides[],
  content: ContentBounds,
): RectSides[] {
  return lines.map((line, index) => ({
    ...line,
    top:
      index === 0
        ? Math.min(content.top, line.top)
        : (lines[index - 1].bottom + line.top) / 2,
    bottom:
      index === lines.length - 1
        ? Math.max(content.bottom, line.bottom)
        : (line.bottom + lines[index + 1].top) / 2,
  }));
}

/**
 * The candidate sharing the most vertical extent with a line.
 * @param line - The line being matched
 * @param candidates - The line boxes to match against
 * @returns The best candidate, or null when none overlaps
 */
export function bestVerticalOverlap(
  line: RectSides,
  candidates: RectSides[],
): RectSides | null {
  const best = candidates.reduce<{ box: RectSides | null; overlap: number }>(
    (found, candidate) => {
      const overlap =
        Math.min(line.bottom, candidate.bottom) -
        Math.max(line.top, candidate.top);
      return overlap > found.overlap ? { box: candidate, overlap } : found;
    },
    { box: null, overlap: 0 },
  );
  return best.box;
}

/**
 * A block's line boxes, measured from its full content and stretched to its
 * content-box bounds.
 * @param block - The paragraph or heading element
 * @returns The block's line boxes, ordered top to bottom
 */
function blockLineBoxes(block: Element): RectSides[] {
  const range = document.createRange();
  range.selectNodeContents(block);
  return expandLinesToContent(
    groupRectsIntoLines(fragmentSides(range)),
    contentBounds(block),
  );
}

/**
 * The vertical bounds of a block's content box.
 * @param block - The block element
 * @returns The content-box top and bottom in viewport space
 */
function contentBounds(block: Element): ContentBounds {
  const rect = block.getBoundingClientRect();
  const style = window.getComputedStyle(block);
  return {
    top:
      rect.top +
      Number.parseFloat(style.borderTopWidth) +
      Number.parseFloat(style.paddingTop),
    bottom:
      rect.bottom -
      Number.parseFloat(style.borderBottomWidth) -
      Number.parseFloat(style.paddingBottom),
  };
}

/**
 * The line-hosting blocks a range touches. The session schema wraps every run
 * of text in a paragraph or heading, including list items and indents.
 * @param dom - The editor's root element
 * @param range - The measured selection
 * @returns The intersecting blocks, in document order
 */
function blocksIntersecting(dom: HTMLElement, range: Range): Element[] {
  return Array.from(dom.querySelectorAll("p, h1, h2, h3, h4, h5, h6")).filter(
    (block) => range.intersectsNode(block),
  );
}

/**
 * A range's fragment rectangles as plain sides, dropping the collapsed ones.
 * @param range - The measured range
 * @returns The visible fragment rectangles
 */
function fragmentSides(range: Range): RectSides[] {
  return Array.from(range.getClientRects())
    .filter((rect) => rect.width > 0 && rect.height > 0)
    .map((rect) => ({
      top: rect.top,
      bottom: rect.bottom,
      left: rect.left,
      right: rect.right,
    }));
}

/**
 * A DOM range spanning two document positions.
 * @param view - The session's editor view
 * @param from - The selection start position
 * @param to - The selection end position
 * @returns The equivalent DOM range
 */
function rangeBetween(view: EditorView, from: number, to: number): Range {
  const range = document.createRange();
  const start = view.domAtPos(from);
  const end = view.domAtPos(to);
  range.setStart(start.node, start.offset);
  range.setEnd(end.node, end.offset);
  return range;
}

/**
 * The union of two rectangles.
 * @param a - One rectangle
 * @param b - The other rectangle
 * @returns The smallest rectangle containing both
 */
function unionOf(a: RectSides, b: RectSides): RectSides {
  return {
    top: Math.min(a.top, b.top),
    bottom: Math.max(a.bottom, b.bottom),
    left: Math.min(a.left, b.left),
    right: Math.max(a.right, b.right),
  };
}
